<template>
  <UiFormLayout :show-footer="showFooter">
    <template v-if="readOnly">
      <UiFormItem :label="t('common.planProperty.category')">
        <div class="plan-readonly-value">{{ categoryReadonlyDisplay }}</div>
      </UiFormItem>
      <UiFormItem v-if="showOrigin" :label="t('common.planProperty.origin')">
        <div class="plan-readonly-value">{{ form.origin }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.planName')">
        <div class="plan-readonly-value">{{ form.planNm }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.executePeriod')">
        <div class="plan-readonly-value">{{ form.executePeriod }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.coop')" class="plan-property-form-item--multiline">
        <div v-if="displayCoopUsers.length" class="plan-readonly-value">
          <PlanUserTagList :users="displayCoopUsers" />
        </div>
        <div v-else class="plan-readonly-value">{{ form.cooperators }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.share')" class="plan-property-form-item--multiline">
        <div v-if="displayShareUsers.length" class="plan-readonly-value">
          <PlanUserTagList :users="displayShareUsers" />
        </div>
        <div v-else class="plan-readonly-value">{{ form.sharers }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.predict')" class="plan-property-form-item--multiline">
        <div class="plan-readonly-value plan-readonly-value--predict">{{ form.predict }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.details')" class="plan-property-form-item--multiline">
        <div class="plan-readonly-value plan-readonly-value--details">{{ form.details }}</div>
      </UiFormItem>
    </template>

    <template v-else>
      <UiFormItem :label="categoryLabel">
        <UiSearchableSelect
          v-if="!isShareLocked"
          class="plan-category-select ui-searchable-select--match-ui-select"
          width="100%"
          :placeholder="t('common.planProperty.categoryPlaceholder')"
          v-model="selectedCategoryId"
          :options="categoryOptions"
          :disabled="readOnly"
        />
        <UiInput v-else :readonly="true" v-model="planData.categoryNm" />
      </UiFormItem>
      <UiFormItem v-if="showCoopShareOrigin" :label="t('common.planProperty.origin')">
        <UiInput :readonly="true" v-model="planData.origin" />
      </UiFormItem>
      <UiFormItem :label="planNameLabel">
        <UiInput
          ref="planNameInputRef"
          :placeholder="t('common.planProperty.planNamePlaceholder')"
          v-model="planData.planNm"
          :max-bytes="200"
          :readonly="isPlanNameLocked"
        />
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.executePeriod')">
        <UiDatePicker
          v-if="!isShareLocked"
          full
          is-range
          split-range
          v-model="executePeriod"
          :start-placeholder="t('common.datePicker.startDate')"
          :end-placeholder="t('common.datePicker.endDate')"
          format="YYYY.MM.DD"
          :disabled="readOnly"
        />
        <UiInput v-else :readonly="true" v-model="executePeriodText" />
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.coop')" :class="{ 'plan-property-form-item--multiline': isShareLocked }">
        <div v-if="!isShareLocked" class="flex gap-5">
          <UiMultiSelect
            :placeholder="t('common.planProperty.coopPlaceholder')"
            :options="coopUserOptions"
            v-model="selectedCooperators"
            tag-variant="gray"
            auto-select-same-dept
            :disabled="readOnly"
          />
          <UiTooltip :title="t('common.planProperty.coopPresetTitle')" position="left">
            <template #trigger>
              <UiButton variant="secondary" icon-only @click="emit('coop-preset', 'C')" :disabled="readOnly">
                <Icon name="heroicons:user" size="20" />
              </UiButton>
            </template>
            <p v-html="t('common.planProperty.coopPresetDesc')" />
          </UiTooltip>
          <UiButton variant="secondary" icon-only @click="emit('open-user-select', 'selectedCooperators')" :disabled="readOnly">
            <Icon name="heroicons:magnifying-glass" size="20" />
          </UiButton>
        </div>
        <div v-else-if="displayCoopUsers.length" class="plan-readonly-text">
          <PlanUserTagList :users="displayCoopUsers" />
        </div>
        <div v-else class="plan-readonly-text">{{ selectedCooperatorsText }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.share')" :class="{ 'plan-property-form-item--multiline': isShareLocked }">
        <div v-if="!isShareLocked" class="flex gap-5">
          <UiMultiSelect
            :placeholder="t('common.planProperty.sharePlaceholder')"
            :options="shareUserOptions"
            v-model="selectedSharers"
            tag-variant="gray"
            auto-select-same-dept
            :disabled="readOnly"
          />
          <UiTooltip :title="t('common.planProperty.sharePresetTitle')" position="left">
            <template #trigger>
              <UiButton variant="secondary" icon-only @click="emit('coop-preset', 'S')" :disabled="readOnly">
                <Icon name="heroicons:user" size="20" />
              </UiButton>
            </template>
            <p v-html="t('common.planProperty.sharePresetDesc')" />
          </UiTooltip>
          <UiButton variant="secondary" icon-only @click="emit('open-user-select', 'selectedSharers')" :disabled="readOnly">
            <Icon name="heroicons:magnifying-glass" size="20" />
          </UiButton>
        </div>
        <div v-else-if="displayShareUsers.length" class="plan-readonly-text">
          <PlanUserTagList :users="displayShareUsers" />
        </div>
        <div v-else class="plan-readonly-text">{{ selectedSharersText }}</div>
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.predict')">
        <UiInput
          :placeholder="isShareLocked ? '' : t('common.planProperty.predictPlaceholder')"
          v-model="planData.predict"
          :max-bytes="4000"
          :readonly="isShareLocked || readOnly"
        />
      </UiFormItem>
      <UiFormItem :label="t('common.planProperty.details')">
        <UiTextarea
          v-model="planData.details"
          :max-bytes="4000"
          :placeholder="isShareLocked ? '' : t('common.planProperty.detailsPlaceholder')"
          :readonly="isShareLocked || readOnly"
        />
      </UiFormItem>
    </template>

    <template v-if="$slots.footerActions" #footerActions>
      <slot name="footerActions" />
    </template>
  </UiFormLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { PlanPropertyForm } from '~/composables/domain/plan/mapPlanToPropertyForm';
import PlanUserTagList from '~/components/domain/Plan/PlanUserTagList.vue';

const { t } = useI18n();

const isDeptUserRow = (user: Record<string, unknown> | null | undefined) => {
  if (!user?.deptId) return false;
  return String(user.userId) === String(user.deptId) || !String(user.userNm || '').trim();
};

const toDisplayUsers = (list: Array<Record<string, unknown>> | undefined) =>
  (list || []).filter(user => user?.userId && !isDeptUserRow(user));

const props = defineProps({
  form: {
    type: Object as () => PlanPropertyForm,
    required: true,
  },
  readOnly: {
    type: Boolean,
    default: true,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  /** readonly HRE/협업은 원본 필드 표시 */
  showOrigin: {
    type: Boolean,
    default: true,
  },
  planData: {
    type: Object,
    default: () => ({}),
  },
  categoryOptions: {
    type: Array,
    default: () => [],
  },
  coopUserOptions: {
    type: Array,
    default: () => [],
  },
  shareUserOptions: {
    type: Array,
    default: () => [],
  },
  /** 신규 등록 시 카테고리 라벨에 필수(*) 표시 */
  showCategoryRequired: {
    type: Boolean,
    default: false,
  },
  /** 공유(S) 업무 + authGbn 03 — 계획 탭 편집 허용 */
  editableSharePlan: {
    type: Boolean,
    default: false,
  },
  coopUserList: {
    type: Array,
    default: () => [],
  },
  shareUserList: {
    type: Array,
    default: () => [],
  },
});

const displayCoopUsers = computed(() => toDisplayUsers(props.coopUserList as Array<Record<string, unknown>>));
const displayShareUsers = computed(() => toDisplayUsers(props.shareUserList as Array<Record<string, unknown>>));

/** 공유 업무 기본 잠금 — editableSharePlan이 true면 협업/내 업무와 동일 편집 UI */
const isShareLocked = computed(() => props.planData?.planType === 'S' && !props.editableSharePlan);
/** AS-IS: 협업(C)/공유(S) 수신 업무는 업무명 편집 비활성 */
const isPlanNameLocked = computed(
  () => props.readOnly || props.planData?.planType === 'C' || props.planData?.planType === 'S',
);

/** 협업(C)·공유(S) 업무는 원본 업무 정보 표시 */
const showCoopShareOrigin = computed(
  () => props.planData?.planType === 'C' || props.planData?.planType === 'S',
);

const withRequiredMark = (label: string) =>
  props.showCategoryRequired && !isShareLocked.value
    ? `${label} <span class='required'>*</span>`
    : label;

const categoryLabel = computed(() => withRequiredMark(t('common.planProperty.category')));
const planNameLabel = computed(() => withRequiredMark(t('common.planProperty.planName')));

// 읽기전용 카테고리 표시 — 보드명 있으면 [보드명] 카테고리명, 없으면 카테고리명만
const categoryReadonlyDisplay = computed(() => {
  const nm = props.form?.categoryNm || '';
  const boardNm = props.form?.boardNm || '';
  return boardNm ? `[${boardNm}] ${nm}` : nm;
});

const selectedCategoryId = defineModel('selectedCategoryId', { type: String, default: '' });
const executePeriod = defineModel('executePeriod', { type: Array, default: () => [] });
const executePeriodText = defineModel('executePeriodText', { type: String, default: '' });
const selectedCooperators = defineModel('selectedCooperators', { type: Array, default: () => [] });
const selectedSharers = defineModel('selectedSharers', { type: Array, default: () => [] });
const selectedCooperatorsText = defineModel('selectedCooperatorsText', { type: String, default: '' });
const selectedSharersText = defineModel('selectedSharersText', { type: String, default: '' });

const emit = defineEmits(['coop-preset', 'open-user-select']);

const planNameInputRef = ref(null);
defineExpose({ planNameInputRef });
</script>

<style scoped lang="scss">
/* 읽기 전용: 입력 박스 없이 텍스트만 표시 */
.plan-readonly-value {
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-gray-90, #1e2124);
  word-break: break-word;
  white-space: pre-wrap;
}

.plan-readonly-value--predict {
  min-height: 96px;
}

.plan-readonly-value--details {
  min-height: 120px;
}

/* 편집 모드 공유(S) 업무: 협업/공유 텍스트 */
.plan-readonly-text {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: 1px solid var(--color-gray-20, #cdd1d5);
  border-radius: 6px;
  background: var(--color-gray-10, #e6e8ea);
  color: var(--color-gray-60, #58616a);
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  white-space: normal;
  min-height: 36px;
}

:deep(.plan-property-form-item--multiline.ui-form-item) {
  align-items: flex-start;

  .ui-form-item__label {
    padding-top: 8px;
  }
}

:deep(.required) {
  color: #ef4444;
}

:deep(.plan-category-select) {
  width: 100% !important;
}
</style>
